// PartLabelTemplate.tsx
import React from "react";
import type { PartLabelDisplay } from "../../models/LabelDisplay";

export type PartLabelTemplateProps = {
  display: PartLabelDisplay;

  // values
  date?: string;
  tagNo?: string;
  logoSrc?: string;
  secondLogoSrc?: string;
  /** Alt text for the second logo - RS Core or PartPro Trade */
  secondLogoAlt?: string;

  make?: string;
  model?: string;
  category?: string;
  stockNumber?: string;
  accountName?: string;

  qrSrc?: string;
  price?: string;

  vrm?: string;
  year?: string;
  engineCode?: string;
  vin?: string;
  fuelType?: string;

  position?: string;
  qc?: string;
  imaged?: string;
};

const textOrNull = (enabled: boolean, value?: string) => {
  const v = (value ?? "").trim();
  return enabled && v ? v : null;
};

export default function PartLabelTemplate(props: PartLabelTemplateProps) {
  const { display } = props;

  // --- Base label design size (what your current measurements are built for)
  const BASE_W = 10; // cm
  const BASE_H = 5;  // cm

  // --- Target label size
  const W = display.widthCm ?? BASE_W;
  const H = display.heightCm ?? BASE_H;

  // --- Scale factors
  const sx = W / BASE_W;
  const sy = H / BASE_H;

  // Use uniform scale for font/pixel-based spacing so it "feels" consistent.
  const s = Math.min(sx, sy);

  // Helpers: scale base cm/px values
  const cw = (cm: number) => `${(cm * sx).toFixed(4)}cm`; // horizontal cm scaling
  const ch = (cm: number) => `${(cm * sy).toFixed(4)}cm`; // vertical cm scaling
  const spx = (px: number) => `${Math.round(px * s)}px`;  // pixel scaling (fonts/gaps)

  const vehicleLines = [
    textOrNull(display.vehicleStack.make, props.make),
    textOrNull(display.vehicleStack.model, props.model),
    textOrNull(display.vehicleStack.vrm, props.vrm),
    textOrNull(display.vehicleStack.year, props.year),
    textOrNull(display.vehicleStack.engineCode, props.engineCode),
    textOrNull(display.vehicleStack.vin, props.vin),
    textOrNull(display.vehicleStack.fuelType, props.fuelType)
  ].filter(Boolean) as string[];

  return (
    <html>
      <head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=no"
        />
      </head>

      <body style={{ margin: 0, padding: 0 }}>
        <div
          style={{
            width: `${W}cm`,
            height: `${H}cm`,
            display: "grid",
            gridTemplateColumns: `${cw(9)} ${cw(1)}`,
            gridTemplateRows: `${ch(1)} ${ch(1)} ${ch(3)}`,
            fontFamily: "Arial,Helvetica,sans-serif",
            boxSizing: "border-box",
            overflow: "hidden"
          }}
        >
          {/* Right vertical date strip (optional) */}
          {display.date && (
            <div
              style={{
                gridColumn: 2 as any,
                gridRow: "1/span 3",
                writingMode: "vertical-rl",
                textOrientation: "mixed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                transform: "rotate(180deg)"
              }}
            >
              {props.date ?? ""}
            </div>
          )}

          {/* Header row */}
          <div
            style={{
              gridColumn: 1 as any,
              gridRow: 1 as any,
              display: "grid",
              gridTemplateColumns: `${cw(4)} ${cw(5)}`
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
                fontWeight: 700,
                fontSize: spx(16),
                height: ch(1)
              }}
            >
              {display.tagNo ? (props.tagNo ?? "") : null}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",   // push logos to the right edge
                paddingLeft: spx(10),         // space away from the tag/title area
                height: ch(1),
              }}
            >
              {display.logo && (props.logoSrc || props.secondLogoSrc) ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: spx(12),             // more breathing room between logos
                    height: "100%",
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  {/* Let a wide logo shrink to the cell rather than being clipped on the left. */}
                  {props.logoSrc ? (
                    <img
                      alt="Logo"
                      src={props.logoSrc}
                      style={{
                        height: "100%",
                        width: "auto",
                        minWidth: 0,
                        flex: "0 1 auto",
                        objectFit: "contain",
                        objectPosition: "right center",
                      }}
                    />
                  ) : null}

                  {props.secondLogoSrc ? (
                    <img
                      alt={props.secondLogoAlt ?? "RS Core"}
                      src={props.secondLogoSrc}
                      style={{
                        height: "85%",         // slightly smaller so it sits nicer next to main logo
                        width: "auto",
                        minWidth: 0,
                        flex: "0 1 auto",
                        objectFit: "contain",
                        objectPosition: "right center",
                      }}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>

          </div>

          {/* Make/Model/Category line */}
          <div
            style={{
              gridColumn: 1 as any,
              gridRow: 2 as any,
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              fontWeight: 700,
              height: ch(1),
              lineHeight: 1,
              fontSize: spx(16),
            }}
          >
            {display.stockNumberLine
              ? (props.stockNumber ?? "").trim() || null
              : display.makeModelCategoryLine
                ? [props.make, props.model].filter(Boolean).join(" ") + (props.category ? ` • ${props.category}` : "")
                : null}
          </div>

          {/* Main row */}
          <div
            style={{
              gridColumn: 1 as any,
              gridRow: 3 as any,
              display: "grid",
              gridTemplateColumns: `${cw(2)} ${cw(4)} ${cw(3)}`
            }}
          >
            {/* Left: QR + price */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  flex: "1 1 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: ch(2),
                  width: cw(2)
                }}
              >
                {display.qr && props.qrSrc ? (
                  <img
                    alt="QR"
                    src={props.qrSrc}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : null}
              </div>

              <div
                style={{
                  fontWeight: 700,
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "end",
                  height: ch(1)
                }}
              >
                {display.price ? (props.price ?? "") : null}
              </div>
            </div>

            {/* Middle: stacked vehicle details */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                gap: spx(2),
                textAlign: "center",
                fontSize: spx(12),
                lineHeight: 1.2,
                height: ch(3)
              }}
            >
              {vehicleLines.map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
              {props.accountName ? (
                <div style={{ fontWeight: 700 }}>{props.accountName}</div>
              ) : null}
            </div>

            {/* Right: position/QC/img */}
            <div
              style={{
                display: "grid",
                gridTemplateRows: `${ch(1.5)} ${ch(1.5)}`,
                gridTemplateColumns: `${cw(1.5)} ${cw(1.5)}`,
                height: "100%"
              }}
            >
              <div
                style={{
                  gridColumn: "1/span 2",
                  display: "flex",
                  alignItems: "end",
                  justifyContent: "center",
                  fontWeight: 700,
                  textAlign: "center"
                }}
              >
                {display.position ? (props.position ?? "-") : null}
              </div>

              <div style={{ display: "flex", alignItems: "end", justifyContent: "center", textAlign: "center" }}>
                {display.qc ? (props.qc ?? "-") : null}
              </div>

              <div style={{ display: "flex", alignItems: "end", justifyContent: "center", textAlign: "center" }}>
                {display.imaged ? (props.imaged ?? "-") : null}
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
